import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import api from '../services/api.js';

const initialState = {
  records: [],
  duplicateRecords: [],
  collectors: [],
  loading: true,
  duplicatesLoading: false,
  isUploading: false,
  message: { text: '', type: '' },
  selectedFile: null,
  selectedRecord: null,
  uploadCollector: '',
  filterCollector: '',
  searchQuery: '',
  selectedRecords: [],
  failedRecords: [],
  modal: {
    isOpen: false,
    title: '',
    message: '',
    confirmText: 'Confirm',
    isDanger: false,
    actionType: null,
    actionPayload: null,
  },
  isMergeModalOpen: false,
  mergePrimaryId: '',
  mergeSubmitting: false,
  page: 1,
  limit: 25,
  totalRecords: 0,
  creationMode: 'upload',
  manualAssignCollector: '',
  viewMode: 'create',
  overdueTasks: [],
  overdueLoading: true,
  overdueError: '',
  seenOverdueCount: 0,
  lastNotifiedOverdue: 0,
};

const computeIsPastDue = (event) => {
  if (!event || !event.scheduledDate) return false;
  if (event.isCompleted) return false;

  const now = new Date();
  const d = new Date(event.scheduledDate);
  const base = new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 0, 0, 0, 0);

  if (event.scheduledTime && typeof event.scheduledTime === 'string') {
    const [hhStr, mmStr] = event.scheduledTime.split(':');
    const hh = Number(hhStr || 0);
    const mm = Number(mmStr || 0);
    const end = new Date(base.getFullYear(), base.getMonth(), base.getDate(), hh, mm + 15, 0, 0);
    return now > end;
  }

  const eod = new Date(base.getFullYear(), base.getMonth(), base.getDate(), 23, 59, 59, 999);
  return now > eod;
};

export const fetchRecords = createAsyncThunk(
  'admin/fetchRecords',
  async (_, { getState, rejectWithValue }) => {
    const { admin } = getState();
    try {
      const params = { page: admin.page, limit: admin.limit };
      if (admin.filterCollector) params.collectorId = admin.filterCollector;
      if (admin.searchQuery) params.search = admin.searchQuery;

      const response = await api.get('/records', { params });
      return {
        records: response.data.data || [],
        total: response.data.total || 0,
      };
    } catch (err) {
      const message = err?.response?.data?.message || 'Failed to fetch records.';
      return rejectWithValue({ message });
    }
  }
);

export const fetchDuplicates = createAsyncThunk(
  'admin/fetchDuplicates',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/records/duplicates');
      return response.data || [];
    } catch (err) {
      const message = err?.response?.data?.message || 'Failed to fetch duplicate records.';
      return rejectWithValue({ message });
    }
  }
);

export const fetchCollectors = createAsyncThunk(
  'admin/fetchCollectors',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/users/collectors');
      return response.data || [];
    } catch (err) {
      const message = err?.response?.data?.message || 'Could not fetch the list of collectors.';
      return rejectWithValue({ message });
    }
  }
);

export const fetchOverdueTasks = createAsyncThunk(
  'admin/fetchOverdueTasks',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/records/overdue-events');
      if (Array.isArray(response.data) && response.data.length > 0) {
        return response.data;
      }

      const now = new Date();
      const start = new Date(now.getFullYear(), now.getMonth() - 2, 1);
      const alt = await api.get('/records/scheduled-events', {
        params: { startDate: start.toISOString(), endDate: now.toISOString() },
      });
      const events = Array.isArray(alt.data) ? alt.data : [];
      return events.filter((event) => computeIsPastDue(event));
    } catch (err) {
      const message = err?.response?.data?.message || 'Failed to fetch overdue tasks.';
      return rejectWithValue({ message });
    }
  }
);

export const uploadRecords = createAsyncThunk(
  'admin/uploadRecords',
  async ({ file, collectorId }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (collectorId) formData.append('collectorId', collectorId);

      const response = await api.post('/records/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    } catch (err) {
      const message = err?.response?.data?.message || 'File upload failed.';
      return rejectWithValue({ message });
    }
  }
);

export const createRecord = createAsyncThunk(
  'admin/createRecord',
  async ({ payload }, { rejectWithValue }) => {
    try {
      await api.post('/records', payload);
      return true;
    } catch (err) {
      const message = err?.response?.data?.message?.join(', ') || 'Unknown error';
      return rejectWithValue({ message });
    }
  }
);

export const assignCollector = createAsyncThunk(
  'admin/assignCollector',
  async ({ recordId, collectorId }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/records/${recordId}/assign`, { collectorId });
      return response.data;
    } catch (err) {
      const message = err?.response?.data?.message || 'Failed to assign collector.';
      return rejectWithValue({ message });
    }
  }
);

export const saveRecord = createAsyncThunk(
  'admin/saveRecord',
  async ({ recordId, data }, { rejectWithValue }) => {
    try {
      const { assignedCollector, comments, _id, __v, ...updatePayload } = data;
      const response = await api.put(`/records/${recordId}`, updatePayload);
      return response.data;
    } catch (err) {
      const message = err?.response?.data?.message || 'Failed to update record.';
      return rejectWithValue({ message });
    }
  }
);

export const fetchRecordById = createAsyncThunk(
  'admin/fetchRecordById',
  async (recordId, { rejectWithValue }) => {
    try {
      const response = await api.get(`/records/${recordId}`);
      return response.data;
    } catch (err) {
      const message = err?.response?.data?.message || 'Could not load the selected record.';
      return rejectWithValue({ message });
    }
  }
);

export const deleteRecords = createAsyncThunk(
  'admin/deleteRecords',
  async ({ recordIds }, { rejectWithValue }) => {
    try {
      await api.delete('/records/delete-many', { data: { ids: recordIds } });
      return recordIds;
    } catch (err) {
      const message = err?.response?.data?.message || 'Failed to delete records.';
      return rejectWithValue({ message });
    }
  }
);

export const reassignRecords = createAsyncThunk(
  'admin/reassignRecords',
  async ({ recordIds, collectorId }, { rejectWithValue }) => {
    try {
      await api.put('/records/reassign-many', { recordIds, collectorId });
      return { recordIds, collectorId };
    } catch (err) {
      const message = err?.response?.data?.message || 'Failed to reassign records.';
      return rejectWithValue({ message });
    }
  }
);

export const mergeDuplicates = createAsyncThunk(
  'admin/mergeDuplicates',
  async ({ primaryId, duplicateIds }, { rejectWithValue }) => {
    try {
      const res = await api.post('/records/duplicates/merge-group', {
        primaryId,
        duplicateIds,
      });
      return { result: res.data, primaryId, duplicateIds };
    } catch (err) {
      const message = err?.response?.data?.message || 'Merge failed. Check backend logs for details.';
      return rejectWithValue({ message });
    }
  }
);

const adminSlice = createSlice({
  name: 'admin',
  initialState,
  reducers: {
    setMessage(state, action) {
      state.message = action.payload;
    },
    clearMessage(state) {
      state.message = { text: '', type: '' };
    },
    setSelectedFile(state, action) {
      state.selectedFile = action.payload;
    },
    setSelectedRecord(state, action) {
      state.selectedRecord = action.payload;
    },
    setUploadCollector(state, action) {
      state.uploadCollector = action.payload;
    },
    setFilterCollector(state, action) {
      state.filterCollector = action.payload;
    },
    setSearchQuery(state, action) {
      state.searchQuery = action.payload;
    },
    setSelectedRecords(state, action) {
      state.selectedRecords = action.payload;
    },
    setFailedRecords(state, action) {
      state.failedRecords = action.payload;
    },
    setModal(state, action) {
      state.modal = action.payload;
    },
    closeModal(state) {
      state.modal = {
        isOpen: false,
        title: '',
        message: '',
        confirmText: 'Confirm',
        isDanger: false,
        actionType: null,
        actionPayload: null,
      };
    },
    setMergeModalOpen(state, action) {
      state.isMergeModalOpen = action.payload;
    },
    setMergePrimaryId(state, action) {
      state.mergePrimaryId = action.payload;
    },
    setMergeSubmitting(state, action) {
      state.mergeSubmitting = action.payload;
    },
    setPage(state, action) {
      state.page = action.payload;
    },
    setLimit(state, action) {
      state.limit = action.payload;
    },
    setCreationMode(state, action) {
      state.creationMode = action.payload;
    },
    setManualAssignCollector(state, action) {
      state.manualAssignCollector = action.payload;
    },
    setViewMode(state, action) {
      state.viewMode = action.payload;
    },
    setSeenOverdueCount(state, action) {
      state.seenOverdueCount = action.payload;
    },
    setLastNotifiedOverdue(state, action) {
      state.lastNotifiedOverdue = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchRecords.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchRecords.fulfilled, (state, action) => {
        state.records = action.payload.records;
        state.totalRecords = action.payload.total;
        state.loading = false;
        state.selectedRecords = [];
      })
      .addCase(fetchRecords.rejected, (state) => {
        state.loading = false;
        state.records = [];
        state.totalRecords = 0;
      })
      .addCase(fetchDuplicates.pending, (state) => {
        state.duplicatesLoading = true;
      })
      .addCase(fetchDuplicates.fulfilled, (state, action) => {
        state.duplicateRecords = action.payload;
        state.duplicatesLoading = false;
        state.selectedRecords = [];
      })
      .addCase(fetchDuplicates.rejected, (state) => {
        state.duplicatesLoading = false;
        state.duplicateRecords = [];
      })
      .addCase(fetchCollectors.fulfilled, (state, action) => {
        state.collectors = action.payload;
      })
      .addCase(fetchOverdueTasks.pending, (state) => {
        state.overdueLoading = true;
        state.overdueError = '';
      })
      .addCase(fetchOverdueTasks.fulfilled, (state, action) => {
        state.overdueTasks = action.payload;
        state.overdueLoading = false;
        state.overdueError = '';
        if (action.payload.length < state.seenOverdueCount) {
          state.seenOverdueCount = action.payload.length;
        }
      })
      .addCase(fetchOverdueTasks.rejected, (state, action) => {
        state.overdueLoading = false;
        state.overdueError = action.payload?.message || 'Failed to fetch overdue tasks.';
      })
      .addCase(uploadRecords.pending, (state) => {
        state.isUploading = true;
      })
      .addCase(uploadRecords.fulfilled, (state) => {
        state.isUploading = false;
      })
      .addCase(uploadRecords.rejected, (state) => {
        state.isUploading = false;
      })
      .addCase(assignCollector.fulfilled, (state, action) => {
        const updated = action.payload;
        state.records = state.records.map((r) => (r._id === updated._id ? updated : r));
        state.duplicateRecords = state.duplicateRecords.map((r) =>
          r._id === updated._id ? updated : r
        );
        if (state.selectedRecord?._id === updated._id) {
          state.selectedRecord = updated;
        }
      })
      .addCase(saveRecord.fulfilled, (state, action) => {
        const updated = action.payload;
        state.records = state.records.map((r) => (r._id === updated._id ? updated : r));
        state.duplicateRecords = state.duplicateRecords.map((r) =>
          r._id === updated._id ? updated : r
        );
        state.selectedRecord = updated;
      })
      .addCase(fetchRecordById.fulfilled, (state, action) => {
        state.selectedRecord = action.payload;
      })
      .addCase(deleteRecords.fulfilled, (state, action) => {
        const ids = new Set(action.payload);
        state.records = state.records.filter((r) => !ids.has(r._id));
        state.duplicateRecords = state.duplicateRecords.filter((r) => !ids.has(r._id));
        state.selectedRecords = [];
      })
      .addCase(reassignRecords.fulfilled, (state) => {
        state.selectedRecords = [];
      })
      .addCase(mergeDuplicates.pending, (state) => {
        state.mergeSubmitting = true;
      })
      .addCase(mergeDuplicates.fulfilled, (state) => {
        state.mergeSubmitting = false;
        state.isMergeModalOpen = false;
        state.selectedRecords = [];
      })
      .addCase(mergeDuplicates.rejected, (state) => {
        state.mergeSubmitting = false;
      });
  },
});

export const showMessage = (text, type = 'error', duration = 5000) => (dispatch) => {
  dispatch(setMessage({ text, type }));
  setTimeout(() => dispatch(clearMessage()), duration);
};

export const {
  setMessage,
  clearMessage,
  setSelectedFile,
  setSelectedRecord,
  setUploadCollector,
  setFilterCollector,
  setSearchQuery,
  setSelectedRecords,
  setFailedRecords,
  setModal,
  closeModal,
  setMergeModalOpen,
  setMergePrimaryId,
  setMergeSubmitting,
  setPage,
  setLimit,
  setCreationMode,
  setManualAssignCollector,
  setViewMode,
  setSeenOverdueCount,
  setLastNotifiedOverdue,
} = adminSlice.actions;

export const selectAdminState = (state) => state.admin;
export const selectAdminRecords = (state) => state.admin.records;
export const selectAdminDuplicateRecords = (state) => state.admin.duplicateRecords;
export const selectAdminCollectors = (state) => state.admin.collectors;
export const selectAdminLoading = (state) => state.admin.loading;
export const selectAdminDuplicatesLoading = (state) => state.admin.duplicatesLoading;
export const selectAdminIsUploading = (state) => state.admin.isUploading;
export const selectAdminMessage = (state) => state.admin.message;
export const selectAdminSelectedFile = (state) => state.admin.selectedFile;
export const selectAdminSelectedRecord = (state) => state.admin.selectedRecord;
export const selectAdminUploadCollector = (state) => state.admin.uploadCollector;
export const selectAdminFilterCollector = (state) => state.admin.filterCollector;
export const selectAdminSearchQuery = (state) => state.admin.searchQuery;
export const selectAdminSelectedRecords = (state) => state.admin.selectedRecords;
export const selectAdminFailedRecords = (state) => state.admin.failedRecords;
export const selectAdminModal = (state) => state.admin.modal;
export const selectAdminMergeModalOpen = (state) => state.admin.isMergeModalOpen;
export const selectAdminMergePrimaryId = (state) => state.admin.mergePrimaryId;
export const selectAdminMergeSubmitting = (state) => state.admin.mergeSubmitting;
export const selectAdminPage = (state) => state.admin.page;
export const selectAdminLimit = (state) => state.admin.limit;
export const selectAdminTotalRecords = (state) => state.admin.totalRecords;
export const selectAdminCreationMode = (state) => state.admin.creationMode;
export const selectAdminManualAssignCollector = (state) => state.admin.manualAssignCollector;
export const selectAdminViewMode = (state) => state.admin.viewMode;
export const selectAdminOverdueTasks = (state) => state.admin.overdueTasks;
export const selectAdminOverdueLoading = (state) => state.admin.overdueLoading;
export const selectAdminOverdueError = (state) => state.admin.overdueError;
export const selectAdminSeenOverdueCount = (state) => state.admin.seenOverdueCount;
export const selectAdminLastNotifiedOverdue = (state) => state.admin.lastNotifiedOverdue;

export default adminSlice.reducer;
